'use client';
import { useState } from 'react';
import { Drawer } from 'vaul';
import type { FormikProps } from 'formik';
import type { LeagueEvent } from '@interfaces/APITravel';
import { Button, Edit02 } from '@v2/ui';
import {
  BaseCheckInOutFilter,
  BaseLocationFilter,
  BaseRoomsFilter,
} from './BaseFilters';
import type { HeaderFormikProps } from './Header';
import {
  coerceHotelSearchArea,
  hotelAreaChipLabel,
} from '../../hotel-search-area';
import { format } from 'date-fns';
import moment from 'moment-timezone';
import pluralize from 'pluralize';

function formatStayDates(start?: string | Date, end?: string | Date): string {
  if (!start) return '';
  const startDate = moment(start).tz('GMT').toDate();
  const endDate = end ? moment(end).tz('GMT').toDate() : null;
  const isValidDate = (d: unknown): d is Date =>
    d instanceof Date && !isNaN(d.getTime());
  if (!isValidDate(startDate)) return '';
  if (endDate && isValidDate(endDate)) {
    const sameYear = startDate.getFullYear() === endDate.getFullYear();
    const startStr = format(startDate, 'MMM d');
    const endStr = format(endDate, sameYear ? 'MMM d' : 'MMM d, yyyy');
    return `${startStr} – ${endStr}`;
  }
  return format(startDate, 'MMM d, yyyy');
}

function formatGuests(
  rooms: HeaderFormikProps['roomConfigurations'] = []
): string {
  const totalAdults = rooms.reduce(
    (acc, r) => acc + (Number(r.adults) || 0),
    0
  );
  const totalChildren = rooms.reduce(
    (acc, r) => acc + (r.children?.length || 0),
    0
  );
  const adultStr = pluralize('adult', totalAdults, true);
  const childStr = pluralize('child', totalChildren, true);
  return `${adultStr}, ${childStr}`;
}

export function MobileFilter({
  event,
  formik,
}: {
  event: LeagueEvent;
  formik: FormikProps<HeaderFormikProps>;
}) {
  const [open, setOpen] = useState(false);

  const venueName = hotelAreaChipLabel({
    area: coerceHotelSearchArea(formik.values.hotelSearchArea),
    venueName: event?.venue?.name,
    city:
      event?.venue?.address?.locality ||
      event?.venue?.location ||
      formik.values.location?.city_name ||
      '',
    customName: formik.values.location?.name,
  });

  const datesStr = formatStayDates(
    formik.values.hotelStartDate,
    formik.values.hotelEndDate
  );
  const guestsStr = formatGuests(formik.values.roomConfigurations);
  const subtitle = datesStr ? `${datesStr} • ${guestsStr}` : guestsStr;

  return (
    <div className="flex flex-col bg-white">
      <h3 className="sr-only">Choose your hotel</h3>

      <Drawer.Root
        open={open}
        onOpenChange={setOpen}
        handleOnly
        repositionInputs={false}
      >
        <Drawer.Trigger asChild>
          <button
            type="button"
            className="mx-4 my-2 flex w-[calc(100%-32px)] items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white p-3 text-left shadow-sm transition hover:border-gray-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500"
          >
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-bold leading-5 text-gray-900 sm:text-base">
                {venueName}
              </span>
              {subtitle && (
                <span className="mt-0.5 block truncate text-xs leading-4 text-gray-600 sm:text-sm">
                  {subtitle}
                </span>
              )}
            </span>
            <Edit02 size="20" className="shrink-0 text-gray-800" />
          </button>
        </Drawer.Trigger>
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 z-40 bg-gray-950/45" />
          <Drawer.Content
            className="fixed inset-x-0 bottom-0 z-50 flex h-[600px] max-h-[95dvh] flex-col rounded-t-2xl bg-white outline-none"
            onPointerDownOutside={(event) => {
              const target = event.target as Element | null;
              if (
                target?.closest?.('[data-radix-popper-content-wrapper]') ||
                target?.closest?.('[role="dialog"]') ||
                target?.closest?.('[data-vaul-no-drag]')
              ) {
                event.preventDefault();
              }
            }}
            onFocusOutside={(event) => {
              const target = event.target as Element | null;
              if (
                target?.closest?.('[data-radix-popper-content-wrapper]') ||
                target?.closest?.('[role="dialog"]') ||
                target?.closest?.('[data-vaul-no-drag]')
              ) {
                event.preventDefault();
              }
            }}
            onInteractOutside={(event) => {
              const target = event.target as Element | null;
              if (
                target?.closest?.('[data-radix-popper-content-wrapper]') ||
                target?.closest?.('[role="dialog"]') ||
                target?.closest?.('[data-vaul-no-drag]')
              ) {
                event.preventDefault();
              }
            }}
          >
            <Drawer.Handle className="mx-auto mb-2 mt-3 h-1.5 w-10 rounded-full bg-gray-300" />
            <div className="flex items-center justify-between px-4 pb-4">
              <Drawer.Title className="text-xl font-bold">
                Edit your stay
              </Drawer.Title>
              <Drawer.Close className="min-h-11 px-3 text-sm font-semibold">
                Close
              </Drawer.Close>
            </div>
            <Drawer.Description className="sr-only">
              Choose your location, dates, and room configuration.
            </Drawer.Description>
            <form
              className="flex min-h-0 flex-1 flex-col justify-between"
              onSubmit={async (event) => {
                event.preventDefault();
                const errors = await formik.validateForm();
                await formik.submitForm();
                if (!Object.keys(errors).length) setOpen(false);
              }}
            >
              <div
                data-vaul-no-drag
                className="no-scrollbar flex flex-col gap-4 overflow-y-auto px-4 pb-4"
              >
                <BaseLocationFilter
                  formik={formik}
                  event={event}
                  variant="inline"
                />
                <BaseCheckInOutFilter
                  formik={formik}
                  event={event}
                  triggerClassName="min-h-[76px]"
                />
                <BaseRoomsFilter
                  formik={formik}
                  triggerClassName="min-h-[76px]"
                />
                {formik.submitCount > 0 &&
                  typeof formik.errors.location === 'string' && (
                    <p role="alert" className="text-sm text-primary-600">
                      {formik.errors.location}
                    </p>
                  )}
                {formik.submitCount > 0 && formik.errors.roomConfigurations && (
                  <p role="alert" className="text-sm text-primary-600">
                    Choose an age for each child before searching.
                  </p>
                )}
              </div>
              <div className="mt-auto border-t p-4 pb-[max(16px,env(safe-area-inset-bottom))]">
                <Button type="submit" className="min-h-12 w-full">
                  Search hotels
                </Button>
              </div>
            </form>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </div>
  );
}
